/**
 * Tournament Standings Computation
 * Calculate and sort group standings based on tournament rules
 */

import { DEFAULT_SCORING, getWalkoverPointsFor } from './gameScoreRules';

/** Rally points credited for walkover/forfeit when no game rows exist (legacy default) */
export const TOURNAMENT_WALKOVER_POINTS_FOR = DEFAULT_SCORING.pointsTarget;
export const TOURNAMENT_WALKOVER_POINTS_AGAINST = 0;

const RESOLVED_MATCH_STATUSES = new Set(['completed', 'walkover', 'forfeit']);

export const isTeamWithdrawn = (team) =>
  Boolean(team && team.withdrawn_at != null && team.withdrawn_at !== '');

const PENDING_GROUP_MATCH_STATUSES = new Set(['scheduled', 'in_progress']);

/**
 * Group matches still awaiting a result (or partial in-progress) involving this team.
 */
export const countPendingGroupMatchesForTeam = (matches, teamId) => {
  if (!teamId || !matches?.length) return 0;
  return matches.filter(
    (m) =>
      m.round === 'group' &&
      PENDING_GROUP_MATCH_STATUSES.has(m.status) &&
      (m.entry1_id === teamId || m.entry2_id === teamId) &&
      m.entry1_id &&
      m.entry2_id
  ).length;
};

/**
 * Group walkover where teamId played and lost (opponent recorded as winner).
 */
export const isGroupWalkoverLostByTeam = (m, teamId) =>
  Boolean(
    teamId &&
      m?.round === 'group' &&
      m?.status === 'walkover' &&
      m.entry1_id &&
      m.entry2_id &&
      m.winner_entry_id &&
      (m.entry1_id === teamId || m.entry2_id === teamId) &&
      m.winner_entry_id !== teamId
  );

/**
 * Group walkover or forfeit involving this team (either side). Used when undoing withdraw
 * so withdrawal-related results clear, including odd manual winners.
 */
export const isGroupWalkoverInvolvingTeam = (m, teamId) =>
  Boolean(
    teamId &&
      m?.round === 'group' &&
      (m?.status === 'walkover' || m?.status === 'forfeit') &&
      m.entry1_id &&
      m.entry2_id &&
      (m.entry1_id === teamId || m.entry2_id === teamId)
  );

/**
 * Match counts toward group standings (W/L, PF/PA, H2H) when decided.
 */
export const isResolvedGroupMatch = (match) =>
  Boolean(
    match &&
      RESOLVED_MATCH_STATUSES.has(match.status) &&
      match.winner_entry_id &&
      match.entry1_id &&
      match.entry2_id
  );

/**
 * Compute standings for a single group
 * 
 * @param {Array} teams - Teams in the group
 * @param {Array} matches - Completed matches in the group
 * @param {Array} games - All session_games for these matches
 * @param {Array<string>} rankingOrder - Ranking criteria (e.g. ['wins', 'point_difference', 'points_scored'])
 * @param {Array<string>} tiebreakerOrder - Tiebreaker criteria
 * @param {{ walkoverPointsFor?: number }} [options]
 * @returns {Array} Sorted standings with stats
 */
export const computeGroupStandings = (teams, matches, games, rankingOrder, tiebreakerOrder, options = {}) => {
  const walkoverPointsFor = options.walkoverPointsFor ?? TOURNAMENT_WALKOVER_POINTS_FOR;
  // Initialize stats for each team
  const teamStats = teams.map(team => ({
    teamId: team.id,
    team,
    matchesPlayed: 0,
    wins: 0,
    losses: 0,
    pointsFor: 0,
    pointsAgainst: 0,
    pointDifference: 0,
    avgPointsPerMatch: 0, // pointsFor / matchesPlayed when complete
    headToHead: {} // { opponentTeamId: { wins, losses, pointDiff } }
  }));
  
  // Create lookup map
  const statsMap = {};
  teamStats.forEach(stats => {
    statsMap[stats.teamId] = stats;
  });
  
  // Process each resolved match (completed, walkover, forfeit)
  for (const match of matches) {
    if (!isResolvedGroupMatch(match)) continue;

    const team1Id = match.entry1_id;
    const team2Id = match.entry2_id;
    const winnerId = match.winner_entry_id;

    const team1Stats = statsMap[team1Id];
    const team2Stats = statsMap[team2Id];

    if (!team1Stats || !team2Stats) continue;

    const matchGames = games.filter((g) => g.tournament_match_id === match.id);

    let team1Points = 0;
    let team2Points = 0;

    if (matchGames.length > 0) {
      for (const game of matchGames) {
        team1Points += game.team_1_score || 0;
        team2Points += game.team_2_score || 0;
      }
    } else if (match.status === 'walkover' || match.status === 'forfeit') {
      if (winnerId === team1Id) {
        team1Points = walkoverPointsFor;
        team2Points = TOURNAMENT_WALKOVER_POINTS_AGAINST;
      } else if (winnerId === team2Id) {
        team1Points = TOURNAMENT_WALKOVER_POINTS_AGAINST;
        team2Points = walkoverPointsFor;
      }
    }
    
    // Update match counts
    team1Stats.matchesPlayed++;
    team2Stats.matchesPlayed++;
    
    // Update wins/losses
    if (winnerId === team1Id) {
      team1Stats.wins++;
      team2Stats.losses++;
    } else if (winnerId === team2Id) {
      team2Stats.wins++;
      team1Stats.losses++;
    }
    
    // Update points
    team1Stats.pointsFor += team1Points;
    team1Stats.pointsAgainst += team2Points;
    team2Stats.pointsFor += team2Points;
    team2Stats.pointsAgainst += team1Points;
    
    // Initialize head-to-head if needed
    if (!team1Stats.headToHead[team2Id]) {
      team1Stats.headToHead[team2Id] = { wins: 0, losses: 0, pointDiff: 0 };
    }
    if (!team2Stats.headToHead[team1Id]) {
      team2Stats.headToHead[team1Id] = { wins: 0, losses: 0, pointDiff: 0 };
    }
    
    // Update head-to-head
    if (winnerId === team1Id) {
      team1Stats.headToHead[team2Id].wins++;
      team2Stats.headToHead[team1Id].losses++;
    } else if (winnerId === team2Id) {
      team2Stats.headToHead[team1Id].wins++;
      team1Stats.headToHead[team2Id].losses++;
    }
    
    team1Stats.headToHead[team2Id].pointDiff += (team1Points - team2Points);
    team2Stats.headToHead[team1Id].pointDiff += (team2Points - team1Points);
  }
  
  // Calculate point differences and normalized rate (rally points scored / completed matches)
  teamStats.forEach((stats) => {
    stats.pointDifference = stats.pointsFor - stats.pointsAgainst;
    stats.avgPointsPerMatch =
      stats.matchesPlayed > 0 ? stats.pointsFor / stats.matchesPlayed : 0;
  });

  const active = teamStats.filter((s) => !isTeamWithdrawn(s.team));
  const withdrawn = teamStats.filter((s) => isTeamWithdrawn(s.team));

  const sortByRules = (arr) =>
    [...arr].sort((a, b) => compareTeams(a, b, rankingOrder, tiebreakerOrder));

  const sortedStats = [...sortByRules(active), ...sortByRules(withdrawn)];

  sortedStats.forEach((stats, index) => {
    stats.position = index + 1;
  });

  return sortedStats;
};

/**
 * Compare two teams based on ranking and tiebreaker rules
 * 
 * @returns {number} Negative if a < b, positive if a > b, 0 if equal
 */
const compareTeams = (a, b, rankingOrder, tiebreakerOrder) => {
  // Primary ranking
  for (const criterion of rankingOrder) {
    const result = compareByCriterion(a, b, criterion);
    if (result !== 0) return result;
  }
  
  // Tiebreaker
  for (const criterion of tiebreakerOrder) {
    const result = compareByCriterion(a, b, criterion);
    if (result !== 0) return result;
  }
  
  // Random tiebreaker (use team ID for consistency)
  return a.teamId.localeCompare(b.teamId);
};

/**
 * Compare two teams by a single criterion (for best losers - no head_to_head)
 */
const compareByCriterionBestLosers = (a, b, criterion) => {
  switch (criterion) {
    case 'wins':
      return b.wins - a.wins;
    case 'point_difference':
      return b.pointDifference - a.pointDifference;
    case 'points_scored':
    case 'points_for':
      return b.pointsFor - a.pointsFor;
    case 'points_against':
      return a.pointsAgainst - b.pointsAgainst; // Fewer against = better
    case 'avg_points_per_match':
      return (b.avgPointsPerMatch || 0) - (a.avgPointsPerMatch || 0);
    case 'random':
      return a.teamId.localeCompare(b.teamId);
    default:
      return 0;
  }
};

/**
 * Compare two teams by a single criterion
 */
const compareByCriterion = (a, b, criterion) => {
  switch (criterion) {
    case 'wins':
      return b.wins - a.wins; // More wins = better
    
    case 'losses':
      return a.losses - b.losses; // Fewer losses = better
    
    case 'point_difference':
      return b.pointDifference - a.pointDifference; // Higher diff = better
    
    case 'points_scored':
    case 'points_for':
      return b.pointsFor - a.pointsFor; // More points = better
    
    case 'points_against':
      return a.pointsAgainst - b.pointsAgainst; // Fewer against = better

    case 'avg_points_per_match':
      return (b.avgPointsPerMatch || 0) - (a.avgPointsPerMatch || 0);

    case 'head_to_head':
      return compareHeadToHead(a, b);
    
    case 'random':
      // Deterministic random based on team IDs
      return a.teamId.localeCompare(b.teamId);
    
    default:
      return 0;
  }
};

/**
 * Compare head-to-head record between two teams
 */
const compareHeadToHead = (a, b) => {
  // Check if they've played each other
  const aVsB = a.headToHead[b.teamId];
  const bVsA = b.headToHead[a.teamId];
  
  if (!aVsB || !bVsA) return 0;
  
  // Compare wins
  const winDiff = aVsB.wins - bVsA.wins;
  if (winDiff !== 0) return winDiff;
  
  // Compare point difference
  return aVsB.pointDiff - bVsA.pointDiff;
};

/**
 * Identify tie-breaker situations where teams have identical stats but the qualification
 * boundary cuts through them (e.g. 2nd qualifies, 3rd doesn't, but they're tied).
 *
 * @param {Array} groupStandings - [{ groupName, groupId, standings }]
 * @param {number} topN - Teams qualifying per group
 * @returns {Array} [{ groupName, groupId, tiedTeams, sharedStats, headToHeadAvailable }]
 */
export const identifyTieBreakerSituations = (groupStandings, topN) => {
  const situations = [];
  if (!groupStandings?.length || topN < 1) return situations;

  for (const { groupName, groupId, standings } of groupStandings) {
    if (!standings?.length) continue;

    // Only non-withdrawn teams matter for qualification ties; use qual-relative positions
    const active = standings.filter((s) => !isTeamWithdrawn(s.team));
    if (active.length === 0) continue;

    const activeWithQualPos = active.map((s, i) => ({ ...s, qualPosition: i + 1 }));

    const clusters = [];
    let cluster = [activeWithQualPos[0]];

    for (let i = 1; i < activeWithQualPos.length; i++) {
      const prev = activeWithQualPos[i - 1];
      const curr = activeWithQualPos[i];
      if (prev.wins === curr.wins) {
        cluster.push(curr);
      } else {
        if (cluster.length > 1) clusters.push([...cluster]);
        cluster = [curr];
      }
    }
    if (cluster.length > 1) clusters.push(cluster);

    for (const clusterTeams of clusters) {
      const positions = clusterTeams.map((t) => t.qualPosition);
      const minPos = Math.min(...positions);
      const maxPos = Math.max(...positions);
      const qualifies = minPos <= topN;
      const doesNotQualify = maxPos > topN;
      if (qualifies && doesNotQualify) {
        let headToHeadAvailable = false;
        for (let i = 0; i < clusterTeams.length; i++) {
          for (let j = i + 1; j < clusterTeams.length; j++) {
            if (clusterTeams[i].headToHead?.[clusterTeams[j].teamId]) {
              headToHeadAvailable = true;
              break;
            }
          }
          if (headToHeadAvailable) break;
        }
        situations.push({
          groupName,
          groupId,
          tiedTeams: clusterTeams,
          sharedStats: {
            wins: clusterTeams[0].wins,
            pointDifference: clusterTeams[0].pointDifference,
            pointsFor: clusterTeams[0].pointsFor,
            pointsAgainst: clusterTeams[0].pointsAgainst
          },
          headToHeadAvailable
        });
      }
    }
  }
  return situations;
};

/**
 * Get bracket size info for knockout progression
 * Knockout brackets require 2, 4, 8, 16, 32... teams (powers of 2)
 * @param {number} groupCount - Number of groups
 * @param {number} topN - Teams qualifying per group
 * @returns {{ totalQualified: number, nextPowerOf2: number, bestLosersNeeded: number, bestLosersPosition: number, isPowerOf2: boolean }}
 */
export const getBracketInfo = (groupCount, topN) => {
  const totalQualified = groupCount * topN;
  const nextPowerOf2 = Math.pow(2, Math.ceil(Math.log2(Math.max(1, totalQualified))));
  const bestLosersNeeded = nextPowerOf2 - totalQualified;
  const bestLosersPosition = topN + 1; // e.g. top 2 qualify → best 3rd place; top 1 → best 2nd place
  const isPowerOf2 = totalQualified > 0 && (totalQualified & (totalQualified - 1)) === 0;
  return { totalQualified, nextPowerOf2, bestLosersNeeded, bestLosersPosition, isPowerOf2 };
};

/**
 * Sort best loser teams by configurable ranking order
 * @param {Array} loserTeams - Teams in position (topN+1) from each group
 * @param {Array} rankingOrder - e.g. ['avg_points_per_match', 'point_difference', 'points_scored', 'wins', 'random']
 */
const sortBestLosers = (
  loserTeams,
  rankingOrder = ['avg_points_per_match', 'point_difference', 'points_scored', 'wins', 'random']
) => {
  return [...loserTeams].sort((a, b) => {
    for (const criterion of rankingOrder) {
      const result = compareByCriterionBestLosers(a, b, criterion);
      if (result !== 0) return result;
    }
    return a.teamId.localeCompare(b.teamId);
  });
};

/** Non-withdrawn rows only, in table order (active teams first). */
export const getActiveStandings = (standings) =>
  (standings || []).filter((s) => !isTeamWithdrawn(s.team));

/**
 * Get qualified teams from all groups
 * 
 * @param {Array} allGroupStandings - Standings for all groups
 * @param {number} topN - Number of teams per group to qualify
 * @param {boolean} includeBestLosers - Whether to include best (topN+1)th place teams to fill bracket
 * @param {Array} bestLosersRankingOrder - Optional. Order to rank best losers. Default: avg_points_per_match, point_difference, points_scored, wins, random
 * @returns {Array} Qualified teams with seed labels
 */
export const getQualifiedTeams = (allGroupStandings, topN, includeBestLosers = false, bestLosersRankingOrder = null) => {
  const qualified = [];
  
  // Top N per group among non-withdrawn teams only
  for (const groupStandings of allGroupStandings) {
    const groupName = groupStandings.groupName;
    const active = getActiveStandings(groupStandings.standings);
    const topTeams = active.slice(0, topN);
    
    topTeams.forEach((teamStats, qualIdx) => {
      const qualRank = qualIdx + 1; // 1 = 1st qualifier from this group, regardless of group stage position
      qualified.push({
        teamId: teamStats.teamId,
        team: teamStats.team,
        groupName,
        position: teamStats.position, // raw group stage position (kept for reference)
        qualRank,
        seedLabel: `${groupName}${qualRank}`, // label reflects qualification rank, not group stage position
        ...teamStats
      });
    });
  }
  
  // Best losers: (topN+1)th among active teams in each group
  const { bestLosersNeeded, bestLosersPosition } = getBracketInfo(allGroupStandings.length, topN);
  if (includeBestLosers && bestLosersNeeded > 0) {
    const loserIndex = topN; // 0-based: next after top N actives
    const loserTeams = [];
    
    for (const groupStandings of allGroupStandings) {
      const active = getActiveStandings(groupStandings.standings);
      const loser = active[loserIndex];
      if (loser) {
        loserTeams.push({
          teamId: loser.teamId,
          team: loser.team,
          groupName: groupStandings.groupName,
          position: bestLosersPosition,
          seedLabel: `${groupStandings.groupName}${bestLosersPosition}`,
          ...loser
        });
      }
    }
    
    const rankingOrder = Array.isArray(bestLosersRankingOrder) && bestLosersRankingOrder.length > 0
      ? bestLosersRankingOrder
      : ['avg_points_per_match', 'point_difference', 'points_scored', 'wins', 'random'];
    const sortedLosers = sortBestLosers(loserTeams, rankingOrder);
    const bestLosers = sortedLosers.slice(0, bestLosersNeeded);
    qualified.push(...bestLosers);
  }
  
  return qualified;
};

/**
 * Calculate match statistics (for completed matches)
 * 
 * @param {Object} match - Tournament match
 * @param {Array} games - Session games for this match
 * @returns {Object} Match statistics
 */
export const calculateMatchStats = (match, games) => {
  if (!games || games.length === 0) {
    return {
      team1GamesWon: 0,
      team2GamesWon: 0,
      team1TotalPoints: 0,
      team2TotalPoints: 0,
      gamesPlayed: 0,
      isComplete: false
    };
  }
  
  let team1GamesWon = 0;
  let team2GamesWon = 0;
  let team1TotalPoints = 0;
  let team2TotalPoints = 0;
  
  for (const game of games) {
    const team1Score = game.team_1_score || 0;
    const team2Score = game.team_2_score || 0;
    
    team1TotalPoints += team1Score;
    team2TotalPoints += team2Score;
    
    if (game.winning_team === 1) team1GamesWon++;
    else if (game.winning_team === 2) team2GamesWon++;
  }
  
  const bestOf = match.best_of;
  const gamesToWin = Math.ceil(bestOf / 2);
  const isComplete = team1GamesWon >= gamesToWin || team2GamesWon >= gamesToWin;
  
  return {
    team1GamesWon,
    team2GamesWon,
    team1TotalPoints,
    team2TotalPoints,
    gamesPlayed: games.length,
    gamesToWin,
    isComplete,
    winnerId: isComplete 
      ? (team1GamesWon > team2GamesWon ? match.entry1_id : match.entry2_id)
      : null
  };
};

/**
 * Validate ranking order configuration
 */
export const validateRankingOrder = (rankingOrder) => {
  const validCriteria = [
    'wins',
    'losses',
    'point_difference',
    'points_scored',
    'points_for',
    'points_against',
    'avg_points_per_match',
    'head_to_head'
  ];
  
  if (!Array.isArray(rankingOrder) || rankingOrder.length === 0) {
    return { valid: false, error: 'Ranking order must be a non-empty array' };
  }
  
  for (const criterion of rankingOrder) {
    if (!validCriteria.includes(criterion)) {
      return { 
        valid: false, 
        error: `Invalid ranking criterion: ${criterion}. Valid options: ${validCriteria.join(', ')}` 
      };
    }
  }
  
  return { valid: true };
};

/**
 * Validate tiebreaker order configuration
 */
export const validateTiebreakerOrder = (tiebreakerOrder) => {
  const validCriteria = [
    'point_difference',
    'points_scored',
    'points_for',
    'avg_points_per_match',
    'head_to_head',
    'random'
  ];
  
  if (!Array.isArray(tiebreakerOrder) || tiebreakerOrder.length === 0) {
    return { valid: false, error: 'Tiebreaker order must be a non-empty array' };
  }
  
  for (const criterion of tiebreakerOrder) {
    if (!validCriteria.includes(criterion)) {
      return { 
        valid: false, 
        error: `Invalid tiebreaker criterion: ${criterion}. Valid options: ${validCriteria.join(', ')}` 
      };
    }
  }
  
  return { valid: true };
};

const RANKING_CRITERION_LABELS = {
  wins: 'Wins',
  losses: 'Losses',
  point_difference: 'Point difference',
  points_scored: 'Points scored',
  points_for: 'Points scored',
  points_against: 'Points against',
  avg_points_per_match: 'Average points per match',
  head_to_head: 'Head-to-head',
  random: 'Team ID (deterministic)'
};

const formatCriteriaList = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((k) => RANKING_CRITERION_LABELS[k] || k)
    .join(' → ');

/**
 * Human-readable summary of ranking and tiebreaker rules for hosts.
 */
export const formatRankingRulesForDisplay = (rankingOrder, tiebreakerOrder) => {
  const primary = formatCriteriaList(rankingOrder);
  const ties = formatCriteriaList(tiebreakerOrder);
  const parts = [];
  if (primary) parts.push(`Order: ${primary}.`);
  if (ties) parts.push(`Ties: ${ties}.`);
  return parts.join(' ');
};
